import { CdkDrag, CdkDragMove, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import { ViewportRuler } from '@angular/cdk/overlay';
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import moment from 'moment';
import { ReorderEventData } from '../io-view-thumbnail/io-view-thumbnail.component';
import {UiDocumentDataModel} from '../../../../../models/document/ui-document-data.model';
import {DocumentDataModel} from '../../../../../models/document/document-data.model';
import {DocumentInFolderDataModel} from '../../../../../models/document/document-in-folder-data.model';
import { RightClickData } from 'src/app/models/ui/rightclickdata';

@Component({
  standalone: false,
  selector: 'app-io-view-pane',
  templateUrl: './io-view-pane.component.html',
  styleUrls: ['./io-view-pane.component.css']
})
export class IoViewPaneComponent implements OnInit, OnChanges {
  @Input() dataset: DocumentInFolderDataModel[] = [];
  @Input() selectedSize: number = 150;
  @Output() onRightClick: EventEmitter<RightClickData> = new EventEmitter<RightClickData>();
  @Output() onDblClick: EventEmitter<DocumentDataModel> = new EventEmitter<DocumentDataModel>();

  moment = moment;
  images: UiDocumentDataModel[] = [];
  selectedDocument: UiDocumentDataModel;

  // **************** DRAG AND DROP PROPERTIES ***************************
  @Output() onReorder: EventEmitter<ReorderEventData> = new EventEmitter<ReorderEventData>();
  @ViewChild(CdkDropListGroup) listGroup: CdkDropListGroup<CdkDropList>;
  @ViewChild(CdkDropList) placeholder: CdkDropList;

  public target: CdkDropList;
  public targetIndex: number;
  public source: CdkDropList;
  public sourceIndex: number;
  public dragIndex: number;
  public activeContainer;
  // **************** END DRAG AND DROP PROPERTIES *********************

  constructor(private viewportRuler: ViewportRuler) { }

  ngOnInit() {
    if (this.images.length !== 0)
      this.selectedDocument = this.images[0];

    this.images.forEach(o => o.selected = false);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dataset) {
      this.images = this.dataset.map(p => p.dataDocument as UiDocumentDataModel);
    }
  }

  // ****************** DRAG AND DROP ***********************************
  // REFERENCE: https://stackblitz.com/edit/angular-dyz1eb
  //            https://v8.material.angular.io/cdk/drag-drop/api
  ngAfterViewInit() {
    let phElement = this.placeholder.element.nativeElement;

    phElement.style.display = 'none';
    phElement.parentElement.removeChild(phElement);
  }

  dragMoved(e: CdkDragMove) {
    let point = this.getPointerPositionOnPage(e.event);

    this.listGroup._items.forEach(dropList => {
      if (__isInsideDropListClientRect(dropList, point.x, point.y)) {
        this.activeContainer = dropList;
        return;
      }
    });
  }

  dropListDropped() {
    if (!this.target)
      return;

    let phElement = this.placeholder.element.nativeElement;
    let parent = phElement.parentElement;

    phElement.style.display = 'none';

    parent.removeChild(phElement);
    parent.appendChild(phElement);
    parent.insertBefore(this.source.element.nativeElement, parent.children[this.sourceIndex]);

    this.target = null;
    this.source = null;

    if (this.sourceIndex != this.targetIndex)
      this.onReorder.emit({ sourceIndex: this.sourceIndex, targetIndex: this.targetIndex });
  }

  dropListEnterPredicate = (drag: CdkDrag, drop: CdkDropList) => {
    if (drop == this.placeholder)
      return true;

    if (drop != this.activeContainer)
      return false;

    let phElement = this.placeholder.element.nativeElement;
    let sourceElement = drag.dropContainer.element.nativeElement;
    let dropElement = drop.element.nativeElement;

    let dragIndex = __indexOf(dropElement.parentElement.children, (this.source ? phElement : sourceElement));
    let dropIndex = __indexOf(dropElement.parentElement.children, dropElement);

    if (!this.source) {
      this.sourceIndex = dragIndex;
      this.source = drag.dropContainer;

      phElement.style.width = sourceElement.clientWidth + 'px';
      phElement.style.height = sourceElement.clientHeight + 'px';

      sourceElement.parentElement.removeChild(sourceElement);
    }

    this.targetIndex = dropIndex;
    this.target = drop;

    phElement.style.display = '';
    dropElement.parentElement.insertBefore(phElement, (dropIndex > dragIndex
      ? dropElement.nextSibling : dropElement));

    this.placeholder._dropListRef.enter(drag._dragRef, drag.element.nativeElement.offsetLeft, drag.element.nativeElement.offsetTop);
    return false;
  }

  /** Determines the point of the page that was touched by the user. */
  getPointerPositionOnPage(event: MouseEvent | TouchEvent) {
    // `touches` will be empty for start/end events so we have to fall back to `changedTouches`.
    const point = __isTouchEvent(event) ? (event.touches[0] || event.changedTouches[0]) : event;
    const scrollPosition = this.viewportRuler.getViewportScrollPosition();

    return {
      x: point.pageX - scrollPosition.left,
      y: point.pageY - scrollPosition.top
    };
  }
  // ****************** END DRAG AND DROP ********************************


  imageClickTimer: any;
  onImageClick(event: MouseEvent, image: UiDocumentDataModel) {
    this.imageClickTimer = setTimeout(() => {
      if (!this.imageClickTimer) return;

      if (!event.ctrlKey)
        this.images.forEach(o => o.selected = false);

      this.selectedDocument = null;

      // this feels so hacky; doing this to force the selected file manager image to refresh
      setTimeout(() => {
        this.selectedDocument = image;
        this.selectedDocument.selected = true;
      }, 0);
    }, 300);
  }

  onImageDblClick(image: UiDocumentDataModel): void {
    clearTimeout(this.imageClickTimer);
    this.imageClickTimer = undefined;

    const selectedImages: UiDocumentDataModel[] = this.images.filter(o => o.selected);
    console.log("selectedImages:", selectedImages);

    this.onDblClick.emit(image);
  }
}



// ******* HELPER FUNCTIONS FOR DRAG AND DROP ********************
function __indexOf(collection, node) {
  return Array.prototype.indexOf.call(collection, node);
};

/** Determines whether an event is a touch event. */
function __isTouchEvent(event: MouseEvent | TouchEvent): event is TouchEvent {
  return event.type.startsWith('touch');
}

function __isInsideDropListClientRect(dropList: CdkDropList, x: number, y: number) {
  const { top, bottom, left, right } = dropList.element.nativeElement.getBoundingClientRect();
  return y >= top && y <= bottom && x >= left && x <= right;
}
// ******* END HELPER FUNCTIONS FOR DRAG AND DROP ********************
